/**
 * Séquence de boot — spec §5.4.
 *
 * 1. entrée CSS unique ;
 * 2. config runtime (throw ⇒ écran d'erreur) ;
 * 3. Keycloak instancié PUIS initialisé, AWAITÉ avant le mount ;
 * 4. client HTTP configuré AVANT toute clé de query, Tolgee lancé AVANT le router (le guard peut traduire une erreur de /api/me) ;
 * 5. Pinia + Pinia Colada + router (créé ICI, après Keycloak — cf. router/index.ts) ;
 * 6. router prêt → mount → boucle de rafraîchissement du token ;
 * 7. le moindre échec monte <BootErrorScreen> à la place de l'app — jamais de page blanche.
 *
 * ⚠️ `vue-sonner/style.css` n'est PAS importé ici : il est porté par
 * `src/components/app/Toaster.vue` (§9, tableau des composants du socle). Un seul
 * des deux endroits, jamais les deux.
 */
import '@/assets/css/theme.css';

import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { PiniaColada } from '@pinia/colada';
import { isNavigationFailure } from 'vue-router';
import App from '@/App.vue';
import BootErrorScreen from '@/features/system/pages/BootErrorScreen.vue';
import { createAppRouter } from '@/router';
import { setupHttp } from '@/api/client';
import { createKeycloak, initKeycloak, startTokenRefreshLoop } from '@/auth/keycloak';
import { tolgee, VueTolgee } from '@/i18n/tolgee';
import { loadRuntimeConfig } from '@/lib/config';

/** `staleTime` global des queries (§7.3). Une query peut le surcharger. */
const DEFAULT_STALE_TIME_MS = 30_000;

async function boot(): Promise<void> {
  const config = await loadRuntimeConfig();

  createKeycloak(config);
  await initKeycloak();

  setupHttp(config.apiBaseUrl);
  await tolgee.run();

  const app = createApp(App);
  app.use(VueTolgee, { tolgee });
  app.use(createPinia());
  app.use(PiniaColada, { queryOptions: { staleTime: DEFAULT_STALE_TIME_MS } });
  const router = createAppRouter();
  app.use(router);

  // Une navigation initiale interrompue par le guard (redirection Keycloak en
  // cours) fait REJETER isReady() avec un NavigationFailure : ce n'est pas une
  // panne de boot, on monte quand même pendant que la page part.
  await router.isReady().catch((error: unknown) => {
    if (isNavigationFailure(error)) return;
    throw error;
  });

  app.mount('#app');
  startTokenRefreshLoop();
}

boot().catch((error: unknown) => {
  const message = error instanceof Error
    ? error.message
    : 'Une erreur inattendue est survenue au démarrage.';
  console.error('[hello-front] échec du démarrage', error);
  createApp(BootErrorScreen, { message }).mount('#app');
});
